import React, { useState } from 'react';
import { config } from '../../config';
import './scss/SubscriptionManagement.scss';
import { CheckCircle, Schedule, Info, Cancel, CreditCard, People, CalendarMonth, Payments } from '../icons';

const SubscriptionManagement = ({ billingInfo, shortId, onUpdate }) => {
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [canceling, setCanceling] = useState(false);
    const [showSeatPurchase, setShowSeatPurchase] = useState(false);

    if (!billingInfo || billingInfo.planType === 'Free') {
        return null;
    }

    const handleCancelSubscription = async () => {
        try {
            setCanceling(true);

            const response = await fetch(`${config.API_URI}/v1/billing/workspace/${shortId}/cancel-subscription`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    cancelImmediately: false, // Always cancel at period end
                    workspaceId: shortId
                })
            });

            const data = await response.json();

            if (response.ok) {
                alert(data.message || 'Subscription will be cancelled at the end of your billing period.');
                setShowCancelConfirm(false);
                if (onUpdate) onUpdate();
            } else {
                alert(data.message || 'Failed to cancel subscription');
            }
        } catch (error) {
            alert('Failed to cancel subscription. Please try again.');
        } finally {
            setCanceling(false);
        }
    };



    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const calculateAvailableSeats = () => {
        const allocated = billingInfo.allocatedSeats || 0;
        const active = billingInfo.activeMembers || 0;
        return Math.max(0, allocated - active);
    };

    const getPlanPrice = (planType, billingCycle) => {
        const prices = {
            'Standard': { monthly: '4.99', annually: '49.99' },
            'Premium': { monthly: '9.99', annually: '95.99' },
            'Enterprise': { monthly: 'Contact sales', annually: 'Contact sales' }
        };

        return prices[planType]?.[billingCycle] || '0.00';
    };

    const isAppSumo = billingInfo.planType === 'AppSumo';
    const isTrialing = billingInfo?.trial?.isTrialing || billingInfo.status === 'trialing';
    const availableSeats = calculateAvailableSeats();

    return (
        <div className="subscription-management">
            <div className="subscription-header">
                <div className="plan-title-row">
                    <h3>{billingInfo.planType}</h3>
                    <span className={`status-pill ${isTrialing ? 'trial' : billingInfo?.billing?.cancelAtPeriodEnd ? 'ending' : 'active'}`}>
                        {isTrialing ? 'Trial' : billingInfo?.billing?.cancelAtPeriodEnd ? 'Ending' : 'Active'}
                    </span>
                </div>

                {isTrialing && (
                    <div className="notice trial">
                        <Info size={16} />
                        <span>Trial ends {formatDate(billingInfo?.trial?.trialEndDate)} · {billingInfo?.trial?.daysRemaining || 0} days left</span>
                    </div>
                )}

                {!isTrialing && billingInfo.billing.cancelAtPeriodEnd && (
                    <div className="notice warning">
                        <Info size={16} />
                        <span>Ends {formatDate(billingInfo?.billing?.currentPeriodEnd || billingInfo?.billing?.nextBillingDate)} · Switches to Free</span>
                    </div>
                )}
            </div>

            <div className="subscription-details">
                {isTrialing ? (
                    <>
                        <div className="detail-row">
                            <span className="label">Active Seats</span>
                            <span className="value">{billingInfo.activeMembers || 0}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Trial Period</span>
                            <span className="value">{formatDate(billingInfo?.trial?.trialStartDate)} → {formatDate(billingInfo?.trial?.trialEndDate)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Days Remaining</span>
                            <span className="value">{billingInfo?.trial?.daysRemaining || 0} days</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">After Trial</span>
                            <span className="value muted">Reverts to Free plan</span>
                        </div>
                    </>
                ) : isAppSumo && billingInfo.appSumo ? (
                    <>
                        <div className="detail-row">
                            <span className="label">Tier</span>
                            <span className="value">{billingInfo.appSumo.tier.replace('tier', 'Tier ')}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Seats</span>
                            <span className="value">{billingInfo.activeMembers || 0} / {billingInfo.appSumo.seats}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Storage</span>
                            <span className="value">{billingInfo.appSumo.storage}GB</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Purchase Date</span>
                            <span className="value">{formatDate(billingInfo.appSumo.purchaseDate)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Billing</span>
                            <span className="value">Lifetime Deal <span className="badge">No fees</span></span>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="detail-row">
                            <span className="label">Seats</span>
                            <span className="value">
                                {billingInfo.activeMembers || 0} / {billingInfo.allocatedSeats || 0}
                                {availableSeats === 0 && (
                                    <button
                                        className="link-btn"
                                        onClick={() => setShowSeatPurchase(true)}
                                    >
                                        Add more
                                    </button>
                                )}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Price per Seat</span>
                            <span className="value">
                                ${getPlanPrice(billingInfo.planType, billingInfo.billingCycle)}/{billingInfo.billingCycle === 'annually' ? 'yr' : 'mo'}
                                {billingInfo.billingCycle === 'annually' && (
                                    <span className="badge">-17%</span>
                                )}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Total Cost</span>
                            <span className="value">
                                ${(getPlanPrice(billingInfo.planType, billingInfo.billingCycle) * (billingInfo.allocatedSeats || 0)).toFixed(2)}/{billingInfo.billingCycle === 'annually' ? 'yr' : 'mo'}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Billing Cycle</span>
                            <span className="value">{billingInfo.billingCycle === 'annually' ? 'Annual' : 'Monthly'}</span>
                        </div>
                        <div className="detail-row">
                            <span className="label">Next Billing</span>
                            <span className="value">{formatDate(billingInfo?.billing?.nextBillingDate || billingInfo?.billing?.currentPeriodEnd)}</span>
                        </div>
                        {/* {billingInfo?.createdAt && (
                            <div className="detail-row">
                                <span className="label">Member Since</span>
                                <span className="value">{formatDate(billingInfo?.createdAt)}</span>
                            </div>
                        )} */}
                    </>
                )}
            </div>

            {!isTrialing && !isAppSumo && !billingInfo?.billing?.cancelAtPeriodEnd && (
                <div className="subscription-actions">
                    {!showCancelConfirm ? (
                        <button
                            className="cancel-btn"
                            onClick={() => setShowCancelConfirm(true)}
                        >
                            Cancel subscription
                        </button>
                    ) : (
                        <div className="cancel-confirm">
                            <div className="confirm-content">
                                <p>Cancel subscription? You'll keep access until {formatDate(billingInfo?.billing?.currentPeriodEnd || billingInfo?.billing?.nextBillingDate)}, then switch to Free plan.</p>
                            </div>
                            <div className="confirm-actions">
                                <button
                                    className="btn-secondary"
                                    onClick={() => setShowCancelConfirm(false)}
                                    disabled={canceling}
                                >
                                    Keep
                                </button>
                                <button
                                    className="btn-danger"
                                    onClick={handleCancelSubscription}
                                    disabled={canceling}
                                >
                                    {canceling ? 'Canceling...' : 'Confirm'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}



          
        </div>
    );
};

export default SubscriptionManagement;
